import { useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import { v4 as uuidv4 } from "uuid";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { POLICY_COMPONENT_TYPE_OPTIONS } from "@constants/policy-component-type-options";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { CreatePolicyComponentConditionContainer } from "@containers/common/rp-service-configuration-policy-decision-container/policy-decision-create-policy-component-container/trigger-configuration/create-policy-component-condition-container/create-policy-component-condition-container";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { TriggerTypes } from "@enums/trigger-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PolicyConfigDecisionAttributeFieldResultTypes } from "@enums/policy-config-decision-attribute-field-result-type";
import { PolicyComponentTypes } from "@enums/policy-component-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { formatRpServiceConfigurationServiceOfferings } from "@formatters/rp-services/rp-service-configuration-service-offering-formatter";
import { FormattedRpServiceConfigurationServiceOfferingAssertion } from "@idw-types/data/rp-service-configuration";
import { AxnAttributeType, AxnAttributeTypeField } from "@idw-types/data/reference-data/axn";
import { AxnAcquiredAttribute } from "@idw-types/data/reference-data/axn/axn-acquired-attribute";
import { ApAssertion, Assertion, AvailableAssertionStatus, DeployedPolicyComponent, ServiceOffering } from "@idw-types/data/reference-data/generic";
import { FormattedPolicyComponent, FormattedPolicyComponentCondition } from "@idw-types/data/reference-data/generic/policy-component";
import { getCreatePolicyComponentValidationErrors } from "@utils/validation/create-policy-component/create-policy-component-validation-util";
import styles from "./policy-component-configuration-tab.less";
import { AttributeClassificationTypeIds } from "@enums/attribute-classification-types";

type PolicyComponentConfigurationTabProps = {
    isDisabled: boolean;
    policyComponentState: FormattedPolicyComponent;
    policyComponents: Array<DeployedPolicyComponent>;
    assertions: Array<Assertion>;
    apAssertions: Array<ApAssertion>;
    serviceOfferings: Array<ServiceOffering>;
    axnAttributeTypes: Array<AxnAttributeType>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    acquiredAttributes: Array<AxnAcquiredAttribute>;
    axnAvailableAssertionStatuses: Array<AvailableAssertionStatus>;
    setPolicyComponentDetailsState: (policyComponentState: FormattedPolicyComponent) => void;
}

export const PolicyComponentConfigurationTab = (props: PolicyComponentConfigurationTabProps) => {
    const {
        isDisabled,
        policyComponentState,
        assertions,
        apAssertions,
        serviceOfferings,
        axnAttributeTypes,
        axnAttributeTypeFields,
        acquiredAttributes,
        policyComponents,
        axnAvailableAssertionStatuses,
        setPolicyComponentDetailsState
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.MEDIUM);
    const [layout] = useState(LayoutTypes.ROW);
    const triggerConfigurationResources = resources.modals.createPolicyComponent.steps.triggerConfiguration;


    const handlePolicyComponentTypeChange = (selectedOption: SelectOption<string>) => {
        setPolicyComponentDetailsState({
            ...policyComponentState,
            type: selectedOption.value
        });
    }

    const addCondition = () => {
        setPolicyComponentDetailsState({
            ...policyComponentState,
            conditions: [
                ...policyComponentState.conditions,
                {
                    assertionID: null,
                    attributeFieldID: null,
                    attributeTypeID: null,
                    componentID: null,
                    id: null,
                    policyComponentID: null,
                    policyConditionOperator: PolicyConfigDecisionAttributeFieldResultTypes.EQUAL_TO,
                    result: null,
                    serviceOfferingID: null,
                    values: null,
                    attributeFieldCategory: null,
                    attributeField2ID: null,
                    attributeField2Category: null,
                    step: null,
                    step2: null,
                    uuid: uuidv4(),
                    triggerType: TriggerTypes.ASSERTION
                }
            ]
        });
    }

    const removeCondition = (uuid: string) => {
        const updatedConditions = policyComponentState.conditions.filter(condition => {
            return (condition.uuid !== uuid);
        });

        setPolicyComponentDetailsState({
            ...policyComponentState,
            conditions: updatedConditions
        });
    }

    const setCondition = (newCondition: FormattedPolicyComponentCondition) => {
        const updatedConditions = policyComponentState.conditions.map((condition) => {
            if (condition.uuid === newCondition.uuid) {
                return newCondition;
            }

            return condition;
        });

        setPolicyComponentDetailsState({
            ...policyComponentState,
            conditions: updatedConditions
        });
    }

    const policyComponentTypeList = useMemo(() => {
        return [...POLICY_COMPONENT_TYPE_OPTIONS, { label: "LIST", value: PolicyComponentTypes.LIST }];
    }, [POLICY_COMPONENT_TYPE_OPTIONS]);


    const selectedPolicyComponentTypeOption = useMemo(() => {
        return policyComponentTypeList.find(option => option.value === policyComponentState.type);
    }, [policyComponentTypeList, policyComponentState.type]);

    const selectedAttributeIds = useMemo(() => {
        return (policyComponentState.conditions || []).map(condition => condition.attributeFieldID).filter(x => x);
    }, [policyComponentState.conditions]);


    const axnAttributeTypeFieldsOnly = useMemo(() => {
        const availableAttributeIds = axnAttributeTypes.filter(attr => [
            AttributeClassificationTypeIds.PII,
            AttributeClassificationTypeIds.DEVICES,
            AttributeClassificationTypeIds.PREFERENCES,
            AttributeClassificationTypeIds.ORGANIZATION,
            AttributeClassificationTypeIds.OTHER,
            AttributeClassificationTypeIds.GOVERNMENT_ISSUED,
            AttributeClassificationTypeIds.CUSTOM
        ].includes(attr.attributeClassificationID)).map(attr => attr.id);

        return axnAttributeTypeFields.filter(atf => !selectedAttributeIds.includes(atf.id) && availableAttributeIds.includes(atf.attributeTypeID));
    }, [axnAttributeTypeFields, axnAttributeTypes, selectedAttributeIds]);

    const axnAcquiredAttributeTypeFields = useMemo(() => {
        const availableAcquiredAttributeTypeIds = acquiredAttributes.map(attr => attr.attributeTypeID);

        return axnAttributeTypeFields.filter(atf => !selectedAttributeIds.includes(atf.id) && availableAcquiredAttributeTypeIds.includes(atf.attributeTypeID));
    }, [acquiredAttributes, selectedAttributeIds]);

    const validationErrors = useMemo(() => {
        return getCreatePolicyComponentValidationErrors(policyComponentState, false);
    }, [policyComponentState]);

    const {
        formattedServiceOfferings,
        formattedAssertions
    } = useMemo(() => {
        const formattedServiceOfferings = formatRpServiceConfigurationServiceOfferings(
            [],
            serviceOfferings,
            apAssertions,
            assertions,
            [],
            []
        ).map(so => ({ ...so, status: EntityStatusTypes.ACTIVE, addedToService: true }));
        const formattedAssertions: Array<FormattedRpServiceConfigurationServiceOfferingAssertion> = [];
        formattedServiceOfferings.forEach(so => {
            formattedAssertions.push(...so.assertions);
        });

        return {
            formattedServiceOfferings,
            formattedAssertions
        };
    }, [serviceOfferings]);

    return (
        <div>
            <ContentPageHeader title={triggerConfigurationResources.title} />
            <ContentPageBody isMaxWidth={true}>
                <SelectFormGroup
                    automationId={`creation-policy-component-trigger-configuration-component-type-select`}
                    controlId={`creation-policy-component-trigger-configuration-component-type-select`}
                    label={triggerConfigurationResources.inputs.componentType.label}
                    ariaLabel={triggerConfigurationResources.inputs.componentType.label}
                    onChange={handlePolicyComponentTypeChange}
                    options={policyComponentTypeList}
                    selectedOption={selectedPolicyComponentTypeOption}
                    disabled={isDisabled}
                    isRequired={true}
                    isSearchable={false}
                    isClearable={false}
                    labelWidth={labelWidth}
                    layout={layout}
                />
                <hr />

                <div>
                    {policyComponentState.conditions.map((condition, index) =>
                        <div key={`${condition.uuid}-${index}`}>
                            <div >
                                <CreatePolicyComponentConditionContainer
                                    index={index}
                                    isDisabled={isDisabled}
                                    condition={condition}
                                    conditions={policyComponentState.conditions}
                                    allAxnAttributeTypes={axnAttributeTypes}
                                    axnAttributeTypes={axnAttributeTypes}
                                    allAxnAttributeTypeFields={axnAttributeTypeFields}
                                    axnAttributeTypeFields={axnAttributeTypeFieldsOnly}
                                    axnAcquiredAttributeTypeFields={axnAcquiredAttributeTypeFields}
                                    allPolicyComponents={policyComponents}
                                    policyComponents={policyComponents}
                                    validationErrors={validationErrors.triggerConfiguration.conditionErrors[condition.uuid]}
                                    assertions={formattedAssertions}
                                    availableAssertions={formattedAssertions}
                                    serviceOfferings={formattedServiceOfferings}
                                    acquiredAttributes={acquiredAttributes}
                                    axnAvailableAssertionStatuses={axnAvailableAssertionStatuses}
                                    setCondition={setCondition}
                                    removeCondition={removeCondition}
                                />
                            </div>
                            {index !== policyComponentState.conditions.length - 1 && selectedPolicyComponentTypeOption && selectedPolicyComponentTypeOption.value !== "LIST" && (
                                <div className={styles.policyComponentTypeOptionText}>
                                    <label>{selectedPolicyComponentTypeOption.value}</label>
                                </div>
                            )}
                        </div>
                    )}
                    {!isDisabled && (
                        <div className={styles.optionContainer}>
                            <Button id="creation-component-trigger-configuration-add-trigger-button" variant="primary" onClick={addCondition}>{"Add a trigger"}</Button>
                        </div>
                    )}
                </div>
            </ContentPageBody>
        </div>
    );
}